import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    Chip,
    IconButton,
    TextField,
    Divider,
    Alert,
    AlertTitle,
    Badge,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Edit as EditIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { jobVacancyService } from '../../services/jobVacancyService';
import { formatCurrency } from '../../utils/format';
import { JobVacancy, VacancyStatus, CreateVacancyData, UpdateVacancyData } from '../../types/jobVacancy';
import VacancyApplications from './VacancyApplications';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { SelectChangeEvent } from '@mui/material/Select';
import HireDialog from '../recruitment/HireDialog';

interface VacancyDetailsProps {
    vacancyId: number;
    open: boolean;
    onClose: () => void;
    onStatusChange?: () => void;
}

const VacancyDetails: React.FC<VacancyDetailsProps> = ({
    vacancyId,
    open,
    onClose,
    onStatusChange
}) => {
    const { t } = useTranslation();
    const [vacancy, setVacancy] = useState<JobVacancy | null>(null);
    const [showApplications, setShowApplications] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [description, setDescription] = useState('');
    const [requirements, setRequirements] = useState('');
    const [loading, setLoading] = useState(false);
    const [pendingApplications, setPendingApplications] = useState(0);
    const [formData, setFormData] = useState<Partial<CreateVacancyData | UpdateVacancyData>>({});
    const [hireOpen, setHireOpen] = useState(false);

    useEffect(() => {
        if (open && vacancyId) {
            loadVacancy();
            loadPendingApplications();
        }
    }, [open, vacancyId]);

    const loadVacancy = async () => {
        try {
            const data = await jobVacancyService.getVacancy(vacancyId);
            setVacancy(data);
            setDescription(data.description || '');
            setRequirements(data.requirements || '');
        } catch (error) {
            console.error('Error loading vacancy:', error);
        }
    };

    const loadPendingApplications = async () => {
        try {
            const count = await jobVacancyService.getPendingApplicationsCount(vacancyId);
            setPendingApplications(count);
        } catch (error) {
            console.error('Error loading pending applications:', error);
        }
    };

    const handleUpdateVacancy = async () => {
        if (!vacancy) return;

        setLoading(true);
        try {
            await jobVacancyService.updateVacancy(vacancyId, {
                description,
                requirements
            });
            await loadVacancy();
            setEditMode(false);
        } catch (error) {
            console.error('Error updating vacancy:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (newStatus: VacancyStatus) => {
        if (!vacancy) return;

        setLoading(true);
        try {
            await jobVacancyService.updateVacancyStatus(vacancyId, newStatus);
            await loadVacancy();
            if (onStatusChange) {
                onStatusChange();
            }
        } catch (error) {
            console.error('Error updating vacancy status:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: VacancyStatus) => {
        switch (status) {
            case VacancyStatus.OPEN:
                return 'success';
            case VacancyStatus.IN_PROGRESS:
                return 'warning';
            case VacancyStatus.CLOSED:
                return 'default';
            case VacancyStatus.CANCELLED:
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    const handleChange = (field: keyof (CreateVacancyData | UpdateVacancyData)) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
    ) => {
        const value = 'type' in event.target && event.target.type === 'checkbox'
            ? (event.target as HTMLInputElement).checked 
            : event.target.value;
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    if (!vacancy) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Detalhes da Vaga</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography variant="h5" gutterBottom>
                                    {vacancy.role}
                                </Typography>
                                <Chip
                                    label={vacancy.status_label}
                                    color={getStatusColor(vacancy.status)}
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    label={vacancy.contract_type}
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                            <Typography variant="h6" color="primary">
                                {formatCurrency(vacancy.base_salary)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                Informações da Vaga
                            </Typography>
                            {!editMode && (
                                <Button
                                    startIcon={<EditIcon />}
                                    onClick={() => setEditMode(true)}
                                    disabled={vacancy.status !== VacancyStatus.OPEN}
                                >
                                    Editar
                                </Button>
                            )}
                        </Box>

                        {editMode ? (
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Descrição"
                                    multiline
                                    rows={4}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    margin="normal"
                                />
                                <TextField
                                    fullWidth
                                    label="Requisitos"
                                    multiline
                                    rows={4}
                                    value={requirements}
                                    onChange={(e) => setRequirements(e.target.value)}
                                    margin="normal"
                                />
                                <Box display="flex" justifyContent="flex-end" mt={2}>
                                    <Button
                                        onClick={() => setEditMode(false)}
                                        sx={{ mr: 1 }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleUpdateVacancy}
                                        disabled={loading}
                                    >
                                        Salvar
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Descrição
                                </Typography>
                                <Typography paragraph>
                                    {vacancy.description || 'Nenhuma descrição disponível.'}
                                </Typography>

                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Requisitos
                                </Typography>
                                <Typography paragraph>
                                    {vacancy.requirements || 'Nenhum requisito especificado.'}
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">
                                Processo Seletivo
                            </Typography>
                            <Box>
                                <Button
                                    startIcon={<PersonIcon />}
                                    onClick={() => setShowApplications(true)}
                                    sx={{ mr: 1 }}
                                >
                                    <Badge badgeContent={pendingApplications} color="error">
                                        Candidaturas
                                    </Badge>
                                </Button>
                                {vacancy.status === VacancyStatus.OPEN && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<CancelIcon />}
                                        onClick={() => handleUpdateStatus(VacancyStatus.CANCELLED)}
                                    >
                                        Cancelar Vaga
                                    </Button>
                                )}
                                {vacancy.status === VacancyStatus.IN_PROGRESS && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() => setHireOpen(true)}
                                    >
                                        Finalizar Processo
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        <Box mt={2}>
                            <Alert severity="info">
                                <AlertTitle>Status do Processo</AlertTitle>
                                {vacancy.status === VacancyStatus.OPEN && (
                                    'Vaga aberta para candidaturas.'
                                )}
                                {vacancy.status === VacancyStatus.IN_PROGRESS && (
                                    'Processo seletivo em andamento.'
                                )}
                                {vacancy.status === VacancyStatus.CLOSED && (
                                    'Processo seletivo finalizado.'
                                )}
                                {vacancy.status === VacancyStatus.CANCELLED && (
                                    'Vaga cancelada.'
                                )}
                            </Alert>
                        </Box>

                        <Box mt={2}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Informações Adicionais
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                        Data de Abertura: {formatDate(vacancy.created_at)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                        Última Atualização: {formatDate(vacancy.updated_at)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            {/* Diálogo de Candidaturas */}
            <VacancyApplications
                vacancyId={vacancyId}
                open={showApplications}
                onClose={() => {
                    setShowApplications(false);
                    loadPendingApplications();
                }}
            />
            {/* Finalizar = registrar quem foi contratado (já aloca a pessoa na posição) */}
            <HireDialog
                open={hireOpen}
                vacancyId={vacancyId}
                vacancyLabel={vacancy?.role}
                onClose={() => setHireOpen(false)}
                onHired={() => {
                    setHireOpen(false);
                    loadVacancy();
                    onStatusChange?.();
                }}
            />
        </Dialog>
    );
};

export default VacancyDetails;